import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Building, Globe, GraduationCap, ArrowRight } from 'lucide-react';

const BU: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Boston University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#CC0000] selection:text-white">
             {/* Main Nav - Heavy Red Header */}
             <div className="bg-white border-b border-gray-200">
                 {/* Top Utility */}
                 <div className="bg-[#2d2926] text-white py-1.5 px-6 flex justify-end gap-6 text-[10px] font-bold uppercase tracking-widest border-b-[6px] border-[#CC0000]">
                     <a href="#" className="hover:text-gray-300">Alumni</a>
                     <a href="#" className="hover:text-gray-300">Community</a>
                     <a href="#" className="hover:text-gray-300">Giving</a>
                     <a href="#" className="hover:text-gray-300">Careers</a>
                 </div>
                 
                 <nav className="py-6 px-6 md:px-12">
                     <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                         <div className="flex flex-col">
                             <span className="font-serif text-3xl font-bold text-[#CC0000] uppercase tracking-wide leading-none">{schoolName}</span>
                         </div>

                         <div className="hidden lg:flex gap-8 font-bold text-[#333] text-sm uppercase tracking-wide">
                             <a href="#" className="hover:text-[#CC0000] transition-colors relative group">Admissions</a>
                             <a href="#" className="hover:text-[#CC0000] transition-colors relative group">Academics</a>
                             <a href="#" className="hover:text-[#CC0000] transition-colors relative group">Research</a>
                             <a href="#" className="hover:text-[#CC0000] transition-colors relative group">Global</a>
                             <a href="#" className="hover:text-[#CC0000] transition-colors relative group">Athletics</a>
                         </div>
                         
                         <div className="flex gap-4 items-center">
                              <Search className="cursor-pointer text-[#333] hover:text-[#CC0000]" size={22} />
                              <Menu className="cursor-pointer lg:hidden text-[#333]" size={24} />
                         </div>
                     </div>
                 </nav>
             </div>

            {/* Hero - City Campus */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1549419168-3e33f374773c?q=80&w=2070&auto=format&fit=crop" 
                    alt="BU Campus on Charles River" 
                    className="absolute inset-0 w-full h-full object-cover grayscale-[20%]"
                />
                 {/* Red Overlay Panel */}
                 <div className="absolute top-0 right-0 md:w-1/3 h-full bg-[#CC0000]/90 p-12 flex flex-col justify-center text-white">
                      <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">
                          Think Big. <br/> Do Good.
                      </h1>
                      <p className="text-lg md:text-xl mb-10 font-light border-t border-white/30 pt-6">
                          We are a leading global research university committed to creating the future.
                      </p>
                      <Button className="bg-white text-[#CC0000] hover:bg-black hover:text-white font-bold uppercase tracking-widest px-8 h-12 rounded-none w-fit">
                          Explore {schoolName.split(' ')[0]}
                      </Button>
                 </div>
            </header>

            {/* Stats / Facts */}
            <section className="bg-[#f2f2f2] py-20 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#333]">
                     <div>
                         <Building size={48} className="text-[#CC0000] mx-auto mb-6" />
                         <h3 className="text-3xl font-bold mb-2">35k+</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Students from 130+ Countries</p>
                     </div>
                      <div>
                         <Globe size={48} className="text-[#CC0000] mx-auto mb-6" />
                         <h3 className="text-3xl font-bold mb-2">Ranked #41</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Global Universities (US News)</p>
                     </div>
                      <div>
                         <GraduationCap size={48} className="text-[#CC0000] mx-auto mb-6" />
                         <h3 className="text-3xl font-bold mb-2">17</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Schools & Colleges</p>
                     </div>
                </div>
            </section>

             {/* BU Today - News Grid */}
             <section className="py-24 px-6 md:px-12 bg-white">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-[#CC0000] pb-4">
                         <h2 className="text-3xl font-sans font-bold text-[#CC0000] uppercase tracking-tight">{schoolName} Today</h2>
                         <a href="#" className="font-bold text-[#333] text-xs uppercase hover:text-[#CC0000] mb-2">More News</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                         <div className="col-span-1 md:col-span-2 relative group cursor-pointer h-[400px] overflow-hidden">
                             <img src="https://images.unsplash.com/photo-1544531327-c45903b22262?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Main Story" />
                             <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-8 text-white">
                                 <span className="bg-[#CC0000] text-white text-[10px] font-bold uppercase px-2 py-1 mb-2 inline-block">Featured</span>
                                 <h3 className="text-3xl font-bold mb-2 group-hover:underline decoration-2 underline-offset-4">University researchers pioneering new treatments for Alzheimer's</h3>
                                 <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest mt-4">
                                     Read Story <ArrowRight size={14}/>
                                 </div>
                             </div>
                         </div>

                         <div className="space-y-8">
                             {[
                                 "Questrom School of Business launches new sustainability MBA",
                                 "BU Hockey team advances to Frozen Four championship",
                                 "School of Public Health hosts global summit on pandemics"
                             ].map((title, i) => (
                                 <div key={i} className="flex gap-4 items-start group cursor-pointer border-b border-gray-100 pb-8 last:border-0 last:pb-0">
                                     <div className="w-24 h-24 bg-gray-100 flex-shrink-0">
                                         <img src={`https://source.unsplash.com/random/200x200?sig=${i+100}`} className="w-full h-full object-cover" alt="Thumbnail"/>
                                     </div>
                                     <div>
                                          <h3 className="text-lg font-bold text-[#333] group-hover:text-[#CC0000] transition-colors leading-snug">{title}</h3>
                                     </div>
                                 </div>
                             ))}
                         </div>
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#2d2926] text-gray-400 py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-bold text-2xl text-white mb-6 block uppercase tracking-wide">{schoolName}</span>
                          <p className="mb-6">
                              Boston, MA 02215 <br/>
                              617-353-2000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest">
                          <ul className="space-y-4 hover:[&_a]:text-[#CC0000]">
                              <li><a href="#">Search</a></li>
                              <li><a href="#">Directory</a></li>
                              <li><a href="#">Maps</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-[#CC0000]">
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Copyright</a></li>
                              <li><a href="#">Accessibility</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-gray-800 pt-8">
                      © 2026 Trustees of {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default BU;
